import { useMemo, useState } from "react";
import { Loader2, Moon, ShieldCheck } from "lucide-react";
import { api, type AppSummary } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type Mode = "off" | "stop";

type PendingChange = {
  app: AppSummary;
  mode: Mode;
};

export function ScenarioAutostop({
  apps,
  scenarioApps,
}: {
  apps: AppSummary[];
  scenarioApps: string[];
}) {
  const workers = useMemo(
    () => scenarioApps.map((name) => apps.find((app) => app.name === name)).filter(Boolean) as AppSummary[],
    [apps, scenarioApps],
  );
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, Mode | undefined>>({});
  const [error, setError] = useState<string | null>(null);

  async function applyChange() {
    if (!pending) return;
    const { app, mode } = pending;
    setPending(null);
    setUpdating(app.name);
    setError(null);
    try {
      const result = await api.setAutostop(app.name, mode);
      setOverrides((current) => ({ ...current, [app.name]: result.autostop }));
    } catch (err) {
      setError(String(err));
    } finally {
      setUpdating(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Scenario workers</CardTitle>
        <CardDescription>
          Keep a worker awake during a class, then restore idle stopping afterward. Autostart stays
          enabled, so the next request wakes a stopped worker.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {workers.map((app) => {
          const mode = overrides[app.name] ?? app.autostop;
          const keepRunning = mode === "off";
          const nextMode: Mode = keepRunning ? "stop" : "off";
          return (
            <div
              key={app.name}
              className="flex flex-wrap items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-3"
            >
              <div className="w-16 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
                {app.env}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-mono text-sm">{app.name}</div>
                <div className="mt-0.5 text-xs text-[var(--color-muted)]">
                  {keepRunning
                    ? "Autostop is disabled; Fly will not stop this worker for idleness."
                    : mode === "stop"
                      ? "Autostop is enabled; Fly may stop this worker when proxy traffic reaches zero."
                      : "Waiting for machine configuration data."}
                </div>
              </div>
              {keepRunning ? (
                <Badge variant="good" className="gap-1.5">
                  <ShieldCheck className="h-3 w-3" /> autostop disabled
                </Badge>
              ) : mode === "stop" ? (
                <Badge variant="warn" className="gap-1.5">
                  <Moon className="h-3 w-3" /> autostop enabled
                </Badge>
              ) : (
                <Badge variant="outline">unknown</Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={updating !== null || mode == null || mode === "suspend"}
                onClick={() => setPending({ app, mode: nextMode })}
              >
                {updating === app.name && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {keepRunning ? "enable autostop" : "disable autostop"}
              </Button>
            </div>
          );
        })}

        {error && (
          <div className="rounded-md border border-[var(--color-bad)]/30 bg-[var(--color-bad)]/10 px-4 py-2 text-sm text-[var(--color-bad)]">
            {error}
          </div>
        )}

        <div className="rounded-md border border-[var(--color-warn)]/30 bg-[var(--color-warn)]/10 px-3 py-2 text-sm text-[var(--color-warn)]">
          If autostop is enabled, Fly can stop the scenario server mid-scenario because the bot
          loop&apos;s outbound WebSocket does not count as inbound service traffic. Disable autostop
          before Play; only enable it after every scenario is paused.
        </div>
      </CardContent>

      <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending?.mode === "off" ? "Disable autostop?" : "Enable autostop?"}
            </DialogTitle>
            <DialogDescription>
              {pending?.mode === "off"
                ? `Fly recreates and starts the ${pending.app.env} scenario Machine. Any scenario currently running on it will end, so make this change before pressing Play.`
                : `Fly recreates and starts the ${pending?.app.env} scenario Machine, ending any scenario currently running on it. With autostop enabled, Fly may stop the Machine mid-scenario whenever inbound traffic falls idle. Only enable autostop after every scenario is paused.`}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 rounded-md border border-[var(--color-warn)]/30 bg-[var(--color-warn)]/10 px-3 py-2 text-sm text-[var(--color-warn)]">
            A later scenario deployment disables autostop again.
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={applyChange}>
              recreate machine
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
